import * as React from 'react';
import * as _ from 'underscore';

import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  DialogPositioningBehavior,
  DialogType,
  TabStyles,
  TextAreaWidget
} from '@tableau/widgets';

import { Messages } from '../../codegen/Localize';
import { cssClass, divWithStyle } from '../../style';
import { ReactModalOptions } from '../../commonInfra/dialogs/ReactModalService';
import { characterCounter } from './CharacterCounter';
import { buttonStyleSubmitting, cancelButtonStyle, textAreaCssClass } from './styles/ExternalContentActionsStyles';

const DESCRIPTION_DIALOG_TEST_ID: string = 'description-dialog';
const DESCRIPTION_DIALOG_CANCEL_TEST_ID: string = DESCRIPTION_DIALOG_TEST_ID + '-cancel';
const DESCRIPTION_DIALOG_SAVE_TEST_ID: string = DESCRIPTION_DIALOG_TEST_ID + '-save';

const MESSAGE_HEIGHT: number = 190;
const DIALOG_WIDTH: number = 500;
export const DESCRIPTION_MAXIMUM_LENGTH = 4000;
export const METRIC_DESCRIPTION_MAXIMUM_LENGTH = 255;

const textAreaClass = textAreaCssClass(DESCRIPTION_DIALOG_TEST_ID);

const textAreaStyle: React.CSSProperties = {
  height: MESSAGE_HEIGHT,
  resize: 'none'
};

const CancelAndSendButtonClass = cssClass('CancelAndSendButtonClass', '', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end'
});

export interface DescriptionDialogProps {
  readonly initial: DescriptionDialogState;
  readonly maxCharacterLimit: number;
  readonly submit: (args: DescriptionDialogState) => void;
  readonly cancel: () => void;
}

export interface DescriptionDialogState {
  readonly description: string;
}

export interface DescriptionDialogStateAndLifecycle extends DescriptionDialogState {
  readonly isSubmitting: boolean;
  readonly isValid: boolean;
}

function checkIsValid(current: DescriptionDialogState, maximumDescriptionLength: number): boolean {
  if (current.description.length > maximumDescriptionLength) {
    return false;
  }

  return true;
}

function checkIsDirty(current: DescriptionDialogState, initial: DescriptionDialogState): boolean {
  return !_.isEqual(initial, current);
}

function getFormState(state: DescriptionDialogState | DescriptionDialogStateAndLifecycle): DescriptionDialogState {
  return {
    description: state.description
  };
}

export class DescriptionDialog extends React.Component<DescriptionDialogProps, DescriptionDialogStateAndLifecycle> {
  public constructor(props: DescriptionDialogProps) {
    super(props);
    this.state = {
      description: props.initial.description || '',
      isValid: undefined,
      isSubmitting: false,
    };
  }

  private onDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const description = event.target.value;

    this.setState((state, props) => {
      const form = getFormState(state);
      const updated: DescriptionDialogState = { ...form, description };
      const isDirty = checkIsDirty(updated, props.initial);
      const isValid = isDirty ? checkIsValid(updated, props.maxCharacterLimit) : undefined;

      return { ...updated, isValid };
    });
  }

  private onSubmit = () => this.setState({ isSubmitting: true }, () => this.props.submit(getFormState(this.state)));

  private onCancel = () => this.setState({ isSubmitting: true }, this.props.cancel);

  public render(): React.ReactNode {
    return (
      <React.Fragment>
        <TextAreaWidget
          className={textAreaClass}
          style={textAreaStyle}
          label={Messages.about()}
          onChange={this.onDescriptionChange}
          value={this.state.description}
          valid={ this.state.isValid ? undefined : this.state.isValid }
          testId={DESCRIPTION_DIALOG_TEST_ID}
          message={characterCounter({value: this.state.description, maximum: this.props.maxCharacterLimit})} />
        <div className={CancelAndSendButtonClass}>
          <ButtonWidget testId={DESCRIPTION_DIALOG_CANCEL_TEST_ID}
            buttonType={ButtonType.Outline}
            disabled={this.state.isSubmitting}
            handleClick={this.onCancel}
            style={cancelButtonStyle}>
            {Messages.cancel()}
          </ButtonWidget>
          <ButtonWidget testId={DESCRIPTION_DIALOG_SAVE_TEST_ID}
            buttonType={ButtonType.Go}
            disabled={this.state.isSubmitting || !this.state.isValid}
            style={ this.state.isSubmitting ? buttonStyleSubmitting : undefined }
            handleClick={this.onSubmit}>
            {
              this.state.isSubmitting ?
                <div style={{ marginTop: TabStyles.Spacing.SmallSpace! }}>
                  <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Small} testId="submitting" />
                </div> :
                <span>{Messages.save()}</span>
            }
          </ButtonWidget>
        </div>
      </React.Fragment>
    );
  }
}

export function descriptionDialogProps(props: DescriptionDialogProps): ReactModalOptions {
  return {
    title: Messages.editDescription2(),
    testId: DESCRIPTION_DIALOG_TEST_ID,
    type: DialogType.NormalWithTitleBar,
    positioningBehavior: DialogPositioningBehavior.Centered,
    dialogWidth: DIALOG_WIDTH,
    dialogContent: <DescriptionDialog {...props} />
  };
}
